import type { JSX } from 'react'
import { Link } from '@tanstack/react-router'
import { Mail, Pencil, RefreshCw, Trash2 } from 'lucide-react'
import type { McAccountStatus } from '@apiary/shared'
import type { McAccountDto } from '@/lib/dto'
import { cn } from '@/lib/cn'
import { dateTimeOf } from '@/ui/format'
import { Button, IconButton } from '@/ui/primitives/Button'
import { Chip } from '@/ui/primitives/Chip'
import { SkinHead } from '@/ui/primitives/SkinHead'
import type { StatusTone } from '@/ui/primitives/StatusDot'
import { OnTop, Surface } from '@/ui/primitives/Surface'

/**
 * Карточка Minecraft-аккаунта.
 *
 * Решения, которые здесь важны:
 *
 *  1. Первой стоит 3D-голова из НАСТОЯЩЕГО скина: в списке из десяти аккаунтов
 *     лицо узнаётся быстрее любой подписи, а у offline-аккаунта силуэт сразу
 *     говорит «этот в Mojang не входил».
 *  2. Почта Microsoft — отдельной строкой во всю ширину. В две колонки она
 *     обрезается на `@`, а различают аккаунты именно по ней.
 *  3. Карточка кликабельна целиком и открывает правку: у аккаунта нет своей
 *     страницы, и «клик по карточке» логично ведёт туда, куда и карандаш.
 *     Кнопки внутри поднятые (`OnTop`), иначе их перехватила бы ссылка.
 *  4. Срок действия токена показан всегда: истёкший — с явным словом «истёк», а
 *     не только красным цветом.
 */

export interface AccountTileProps {
  account: McAccountDto
  canOperate: boolean
  /** Идёт запрос устройства кода именно для этого аккаунта. */
  reauthing?: boolean
  onReauth: () => void
  onEdit: () => void
  onDelete: () => void
}

const STATUS: Record<McAccountStatus, { label: string; tone: StatusTone; pulse?: boolean }> = {
  NEW: { label: 'Не подключён', tone: 'idle' },
  AUTH_PENDING: { label: 'Ждёт входа', tone: 'busy', pulse: true },
  READY: { label: 'Готов', tone: 'live' },
  AUTH_FAILED: { label: 'Ошибка входа', tone: 'fail' },
  EXPIRED: { label: 'Токен истёк', tone: 'warn' },
  DISABLED: { label: 'Отключён', tone: 'idle' }
}

export function AccountTile({
  account,
  canOperate,
  reauthing = false,
  onReauth,
  onEdit,
  onDelete
}: AccountTileProps): JSX.Element {
  const status = STATUS[account.status]
  const microsoft = account.authType === 'MICROSOFT'
  const lockedByBotId = account.lockedByBotId
  const expiresAt = dateTimeOf(account.tokenExpiresAt)
  const expired = account.tokenExpiresAt !== null && Date.parse(account.tokenExpiresAt) < Date.now()
  const noRights = canOperate ? undefined : 'Недостаточно прав'

  return (
    <Surface
      variant="raised"
      onActivate={onEdit}
      // Подпись растянутой кнопки отличается от подписи карандаша: два элемента
      // с одинаковым именем в одной карточке скринридер читает как дубль.
      activateLabel={`Открыть аккаунт ${account.label}`}
      className="flex h-full flex-col"
      data-account-id={account.id}
    >
      <div className="flex min-w-0 items-start gap-3 px-3.5 pt-3">
        <SkinHead
          uuid={account.mcUuid}
          name={account.gamertag ?? account.label}
          size={38}
          spin={account.status === 'READY'}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-md font-semibold text-ink">{account.label}</p>
          <p className="truncate font-mono text-2xs text-ink-muted">
            {account.gamertag ?? (microsoft ? 'ник появится после входа' : 'ник не задан')}
          </p>
        </div>
        <Chip tone={status.tone} dot pulse={status.pulse === true}>
          {status.label}
        </Chip>
      </div>

      <p className="mt-3 flex min-w-0 items-center gap-2 px-3.5 text-xs">
        <Mail aria-hidden className="size-3.5 shrink-0 text-ink-faint" />
        {/* Даже заглушка «почта не указана» — обычный muted, а не faint: faint по
            токенам разрешён только иконкам и декору (контраст 3.6:1). */}
        <span className="truncate font-mono text-ink-muted">
          {microsoft ? (account.msaEmail ?? 'почта не указана') : 'вход без Microsoft'}
        </span>
      </p>

      <dl className="mt-3 grid grid-cols-1 gap-1 px-3.5 text-2xs">
        <Fact label="Авторизация" value={microsoft ? 'Microsoft' : 'Offline (без Mojang)'} />
        <Fact label="UUID" value={account.mcUuid} mono />
        <Fact
          label="Токен"
          value={
            expiresAt === null
              ? microsoft
                ? 'нет — нужен вход'
                : 'не нужен'
              : expired
                ? `истёк ${expiresAt}`
                : `до ${expiresAt}`
          }
          tone={expired ? 'warn' : undefined}
        />
      </dl>

      {/* Ряд меток рисуем только когда в нём что-то есть: пустой блок с
          отступом делал карточки визуально разной плотности. */}
      <div className="mt-2 flex min-w-0 flex-wrap gap-1.5 px-3.5 empty:hidden">
        {lockedByBotId !== null ? (
          // Ссылка, а не просто метка: первый вопрос при виде «занят» — «кем?».
          <OnTop>
            <Link
              to="/bots/$botId"
              params={{ botId: lockedByBotId }}
              className="inline-flex items-center gap-1.5 rounded-pill border border-hold/35 bg-hold/12 px-2 py-0.5 text-2xs text-hold hover:border-hold"
            >
              <span aria-hidden className="size-2 rounded-pill bg-hold" />
              Занят ботом — открыть
            </Link>
          </OnTop>
        ) : null}
        {account.status === 'EXPIRED' || account.status === 'AUTH_FAILED' ? (
          <Chip tone="warn">Нужна переавторизация</Chip>
        ) : null}
      </div>

      <OnTop className="mt-auto flex items-center gap-1 border-t border-line px-2 py-1.5">
        <Button
          size="sm"
          variant="secondary"
          loading={reauthing}
          disabled={!canOperate || !microsoft}
          title={
            noRights ??
            (microsoft ? 'Запросить новый код входа Microsoft' : 'Offline-аккаунту вход не нужен')
          }
          onClick={onReauth}
        >
          <RefreshCw aria-hidden className="size-3.5" />
          Переавторизовать
        </Button>

        <IconButton
          label={`Изменить аккаунт ${account.label}`}
          size="sm"
          className="ml-auto"
          disabled={!canOperate}
          onClick={onEdit}
        >
          <Pencil aria-hidden className="size-4" />
        </IconButton>
        <IconButton
          label={`Удалить аккаунт ${account.label}`}
          size="sm"
          tone="danger"
          disabled={!canOperate || lockedByBotId !== null}
          title={lockedByBotId === null ? undefined : 'Аккаунт занят ботом — сначала удалите бота'}
          onClick={onDelete}
        >
          <Trash2 aria-hidden className="size-4" />
        </IconButton>
      </OnTop>
    </Surface>
  )
}

function Fact({
  label,
  value,
  mono = false,
  tone
}: {
  label: string
  value: string | null
  mono?: boolean
  tone?: 'warn'
}): JSX.Element {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3">
      <dt className="shrink-0 text-ink-muted">{label}</dt>
      <dd
        className={cn(
          'truncate',
          mono && 'font-mono tabular-nums',
          value === null ? 'text-ink-faint' : tone === 'warn' ? 'text-warn' : 'text-ink'
        )}
      >
        {value ?? '—'}
      </dd>
    </div>
  )
}
